import { useState, useEffect, useCallback } from "react";
import { getAuthToken } from "../lib/api";

interface NotificationStatus {
  botConnected: boolean;
  botUsername: string | null;
  lastSent: string | null;
  lastSentMessage: string | null;
  activeSchedules: number;
  totalSent24h: number;
}

interface NotificationEntry {
  id: number;
  agent_id: string;
  message: string;
  status: string;
  error_message: string | null;
  created_at: string;
  agent_name: string;
  project_name: string | null;
  project_color: string | null;
}

export function useNotificationStatus(pollIntervalMs = 30000) {
  const [status, setStatus] = useState<NotificationStatus | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchStatus = useCallback(async () => {
    try {
      const token = getAuthToken();
      const headers: Record<string, string> = {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      };
      const res = await fetch("/api/notifications/status", { headers });
      if (res.ok) {
        const data = await res.json();
        setStatus(data);
      }
    } catch {
      // silently fail
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStatus();
    const interval = setInterval(fetchStatus, pollIntervalMs);
    return () => clearInterval(interval);
  }, [fetchStatus, pollIntervalMs]);

  return { status, loading, refresh: fetchStatus };
}

export function useNotificationFeed(limit = 20) {
  const [notifications, setNotifications] = useState<NotificationEntry[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchFeed = useCallback(async () => {
    try {
      const token = getAuthToken();
      const headers: Record<string, string> = {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      };
      const res = await fetch(`/api/notifications/recent?limit=${limit}`, { headers });
      if (res.ok) {
        const data = await res.json();
        setNotifications(data.notifications);
      }
    } catch {
      // silently fail
    } finally {
      setLoading(false);
    }
  }, [limit]);

  useEffect(() => {
    fetchFeed();
  }, [fetchFeed]);

  return { notifications, loading, refresh: fetchFeed };
}
